import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import download from "../../../../../assets/images/fileManager/Download.svg";
import printer from "../../../../../assets/images/fileManager/Printer.svg";
import save from "../../../../../assets/images/fileManager/Save.svg";
import arrowLeft from "../../../../../assets/images/svg/arrow-left.svg";
import forward from "../../../../../assets/images/svg/forward.svg";
import backward from "../../../../../assets/images/svg/backward.svg";
import { ReactComponent as More } from "../../../../../assets/images/fileManager/VerticalMoreWhite.svg";
import ImageReader from "./resourceReaders/image/imageReader.index";
import { PreviewModalPropsTypes } from "./previewModal.interface";
import SVGReader from "./resourceReaders/svg/SVGReader";
import AudioPlayer from "react-awesome-audio-player";
import "react-awesome-audio-player/styles.min.css";
import {
    getFileExtension,
    imageFormatList,
    musicFormatList,
    setIconFile,
    videoFormatList,
    separateNameAndFormat,
    formatsHasPrint,
    getWebEnvConfig,
    getFileManagerParent
} from "../../../../../scripts/helpers";
import PATH from "../../../../../urls/path";
import { ResourceAccessEnum, ResourceType } from "../../../../../interfaces/general";
import {
    handleClearAllSelectedResources,
    handleSelectResources,
    handleSetDownloadModalStatus,
    handleSetInformationModalStatus,
    handleSetIsSingleSelect,
    handleSetPreviewModalStatus
} from "../../fileManager.actions";
import { contextMenuState } from "../contextMenu/contextMenu.interface";
import { downloadResource, setContextMenu } from "../../fileManager.function";
import ReactToPrint, { useReactToPrint } from "react-to-print";
import { zIndexs } from "../../../../../interfaces/environment";
import { useAppSelector } from "../../../../../redux/hooks";
import { encode } from "js-base64";
import { useLocation, useSearchParams } from "react-router-dom";
import DownloadAlertModal from "../modals/donwloadAlertModal/downloadAlertModal.index";
import TxtReader from "./resourceReaders/txt/txtReader.index";
import { authToken } from "../../../../storage/storage";
import { handleSetFileUploadState } from "../../../../../scripts/upload";
import { shallowEqual } from "react-redux";
import { lazy, Suspense } from "react";

const PdfReader = lazy(() => import("./resourceReaders/pdf/pdfReader.index"));

const PreviewModal = ({ resource, filteredResources }: PreviewModalPropsTypes) => {
    const { informationModalStatus, selectedResources, previewModalStatus } = useAppSelector(
        ({ fileManager: { informationModalStatus, selectedResources, previewModalStatus } }) => ({
            informationModalStatus,
            selectedResources,
            previewModalStatus
        }),
        shallowEqual
    );
    const { authStatus } = useAppSelector(({ auth: { authStatus } }) => ({ authStatus }), shallowEqual);
    const [resourceIndex, setResourceIndex] = useState(filteredResources.indexOf(resource));
    const [zIndexValue, setZIndexValue] = useState<any>(zIndexs.previewModal);
    const [filteredResource, setFilteredResource] = useState<ResourceType>(resource);
    const [resourceReader, setResourceReader] = useState<any>();
    const [scale, setScale] = useState<number>(1);
    const [isLoading, setIsLoading] = useState(false);
    const [txtFileValue, setTxtFileValue] = useState("");
    const [editedTxtFileValue, setEditedTxtFileValue] = useState("");
    const location = useLocation();
    const [, setSearchParams] = useSearchParams();

    useEffect(() => {
        if (informationModalStatus) setZIndexValue(zIndexs.previewModal2);
    }, []);

    const previewVideoFormats = (realFormat: string) => {
        if (["mp4", "mkv", "mov", "m4v"].includes(realFormat)) return "mp4";
        else if (realFormat === "webm" || realFormat === "flv") return realFormat;
        return false;
    };

    useEffect(() => {
        const format = getFileExtension(filteredResource.name);
        if (imageFormatList.includes(format!) && format === "svg") setResourceReader("svg");
        else if (imageFormatList.includes(format!)) setResourceReader("image");
        else if (musicFormatList.includes(format!)) setResourceReader("audio");
        else if (format === "pdf") setResourceReader("pdf");
        else if (format === "txt") setResourceReader("txt");
        else if (videoFormatList.includes(format!) && previewVideoFormats(format!)) {
            setResourceReader("video");
        }
    }, [resource, filteredResource.name]);

    const handleChangeResource = useCallback(
        (type: string) => {
            if (type === "previous" && resourceIndex !== 0) {
                handleSelectResources([filteredResources.filter((resource) => getFileExtension(resource.name) !== "xlsx")[resourceIndex - 1]]);
                setFilteredResource(filteredResources.filter((resource) => getFileExtension(resource.name) !== "xlsx")[resourceIndex - 1]);
                setResourceIndex(resourceIndex - 1);
            } else if (type === "next" && resourceIndex !== filteredResources.length - 1) {
                handleSelectResources([filteredResources.filter((resource) => getFileExtension(resource.name) !== "xlsx")[resourceIndex + 1]]);
                setFilteredResource(filteredResources.filter((resource) => getFileExtension(resource.name) !== "xlsx")[resourceIndex + 1]);
                setResourceIndex(resourceIndex + 1);
            }
        },
        [filteredResources, resourceIndex]
    );

    const handlePopupContent = () => {
        if (location.pathname === PATH.shared) {
            if (selectedResources[0].shareData!.access === ResourceAccessEnum.WRITE) {
                if (!getFileManagerParent()) {
                    if (getFileExtension(selectedResources[0].name) === "pdf") {
                        if (resourceIndex === 0 && resourceIndex === filteredResources.length - 1) {
                            return [...contextMenuState.previewRootShareWriteAccess, "PRINT", "DISABLE_NEXT_FILE", "DISABLE_PREVIOUS_FILE"];
                        } else if (resourceIndex === 0) {
                            return [...contextMenuState.previewRootShareWriteAccess, "PRINT", "NEXT_FILE", "DISABLE_PREVIOUS_FILE"];
                        } else if (resourceIndex === filteredResources.length - 1) {
                            return [...contextMenuState.previewRootShareWriteAccess, "PRINT", "DISABLE_NEXT_FILE", "PREVIOUS_FILE"];
                        } else {
                            return [...contextMenuState.previewRootShareWriteAccess, "PRINT", "NEXT_FILE", "PREVIOUS_FILE"];
                        }
                    } else {
                        return contextMenuState.previewRootShareWriteAccess;
                    }
                } else {
                    if (getFileExtension(selectedResources[0].name) === "pdf") {
                        if (resourceIndex === 0 && resourceIndex === filteredResources.length - 1) {
                            return [...contextMenuState.previewShareWriteAccess, "PRINT", "DISABLE_NEXT_FILE", "DISABLE_PREVIOUS_FILE"];
                        } else if (resourceIndex === 0) {
                            return [...contextMenuState.previewShareWriteAccess, "PRINT", "NEXT_FILE", "DISABLE_PREVIOUS_FILE"];
                        } else if (resourceIndex === filteredResources.length - 1) {
                            return [...contextMenuState.previewShareWriteAccess, "PRINT", "DISABLE_NEXT_FILE", "PREVIOUS_FILE"];
                        } else {
                            return [...contextMenuState.previewShareWriteAccess, "PRINT", "NEXT_FILE", "PREVIOUS_FILE"];
                        }
                    } else {
                        return contextMenuState.previewShareWriteAccess;
                    }
                }
            } else {
                if (getFileExtension(selectedResources[0].name) === "pdf") {
                    if (resourceIndex === 0 && resourceIndex === filteredResources.length - 1) {
                        return [...contextMenuState.previewShareReadAccess, "PRINT", "DISABLE_NEXT_FILE", "DISABLE_PREVIOUS_FILE"];
                    } else if (resourceIndex === 0) {
                        return [...contextMenuState.previewShareReadAccess, "PRINT", "NEXT_FILE", "DISABLE_PREVIOUS_FILE"];
                    } else if (resourceIndex === filteredResources.length - 1) {
                        return [...contextMenuState.previewShareReadAccess, "PRINT", "DISABLE_NEXT_FILE", "PREVIOUS_FILE"];
                    } else {
                        return [...contextMenuState.previewShareReadAccess, "PRINT", "NEXT_FILE", "PREVIOUS_FILE"];
                    }
                } else {
                    return contextMenuState.previewShareReadAccess;
                }
            }
        } else {
            if (selectedResources[0].is_favorite) {
                if (getFileExtension(selectedResources[0].name) === "pdf") {
                    if (resourceIndex === 0 && resourceIndex === filteredResources.length - 1) {
                        return [...contextMenuState.previewWithFavorite, "PRINT", "DISABLE_NEXT_FILE", "DISABLE_PREVIOUS_FILE"];
                    } else if (resourceIndex === 0) {
                        return [...contextMenuState.previewWithFavorite, "PRINT", "NEXT_FILE", "DISABLE_PREVIOUS_FILE"];
                    } else if (resourceIndex === filteredResources.length - 1) {
                        return [...contextMenuState.previewWithFavorite, "PRINT", "DISABLE_NEXT_FILE", "PREVIOUS_FILE"];
                    } else {
                        return [...contextMenuState.previewWithFavorite, "PRINT", "NEXT_FILE", "PREVIOUS_FILE"];
                    }
                } else {
                    return contextMenuState.previewWithFavorite;
                }
            } else {
                if (getFileExtension(selectedResources[0].name) === "pdf") {
                    if (resourceIndex === 0 && resourceIndex === filteredResources.length - 1) {
                        return [...contextMenuState.previewWithoutFavorite, "PRINT", "DISABLE_NEXT_FILE", "DISABLE_PREVIOUS_FILE"];
                    } else if (resourceIndex === 0) {
                        return [...contextMenuState.previewWithoutFavorite, "PRINT", "NEXT_FILE", "DISABLE_PREVIOUS_FILE"];
                    } else if (resourceIndex === filteredResources.length - 1) {
                        return [...contextMenuState.previewWithoutFavorite, "PRINT", "DISABLE_NEXT_FILE", "PREVIOUS_FILE"];
                    } else {
                        return [...contextMenuState.previewWithoutFavorite, "PRINT", "NEXT_FILE", "PREVIOUS_FILE"];
                    }
                } else {
                    return contextMenuState.previewWithoutFavorite;
                }
            }
        }
    };

    const handleClickMore = (e: any) => {
        e.preventDefault();
        e.stopPropagation();
        const xPosition = e.pageX;
        const yPosition = e.pageY;
        setContextMenu(
            true,
            {
                x: xPosition,
                y: yPosition + 20
            },
            handlePopupContent()
        );
    };

    const handleClosePreviewModal = useCallback(() => {
        if (!informationModalStatus) {
            handleSelectResources([]);
            handleSetIsSingleSelect(false);
        }

        if (zIndexValue === zIndexs.previewModal) {
            handleSetPreviewModalStatus(false, true);
            const qs = new URLSearchParams(location.search);
            qs.delete("preview");
            setSearchParams(qs);
        } else {
            handleSetInformationModalStatus(false);
            setZIndexValue(zIndexs.previewModal);
        }
    }, [zIndexValue]);

    useEffect(() => {
        const keyboardEventListener = (e: any) => {
            if (e.code === "ArrowRight") handleChangeResource("previous");
            if (e.code === "ArrowLeft") handleChangeResource("next");
            if (e.code === "Escape") handleClosePreviewModal();
        };
        document.body.addEventListener("keydown", keyboardEventListener);
        return () => {
            window.removeEventListener("popstate", () => {
                handleClosePreviewModal();
            });
            document.body.removeEventListener("keydown", keyboardEventListener);
        };
    }, [handleChangeResource, handleClosePreviewModal]);

    window.addEventListener("popstate", () => {
        handleClosePreviewModal();
    });

    useEffect(() => {
        window.addEventListener("popstate", handleClosePreviewModal);
        return () => window.removeEventListener("popstate", handleClosePreviewModal);
    }, [informationModalStatus, zIndexValue]);

    const handleClick = (event: any) => {
        if (event.target.id === "click-close-modal") {
            if (informationModalStatus) {
                handleSetPreviewModalStatus(false, true);
            } else {
                handleSetPreviewModalStatus(false);
            }
            const qs = new URLSearchParams(location.search);
            qs.delete("preview");
            setSearchParams(qs);
        }
    };

    const componentRef = useRef(null);

    const handlePrint = useReactToPrint({
        content: () => componentRef.current
    });

    const handleDownload = () => {
        if (localStorage.getItem("HAMRAHI_DOWNLOAD_ALERT")) {
            downloadResource();
        } else {
            handleSetDownloadModalStatus(true);
        }
    };

    const getTxtValue = async () => {
        setTxtFileValue("");
        setIsLoading(true);
        await fetch(filteredResource.download_url, {
            method: "GET",
            headers: { Authorization: "Bearer " + authToken.get()?.access }
        })
            .then((response) => response.text())
            .then((data) => {
                setTxtFileValue(data);
                setIsLoading(false);
            })
            .catch((error) => console.error("Error fetching data:", error));
    };

    useEffect(() => {
        if (resourceReader === "txt") {
            getTxtValue();
        }
    }, [filteredResource, resourceReader]);

    useEffect(() => {
        setFilteredResource(selectedResources[0]);
    }, [selectedResources]);

    const exportNewTextFile = () => {
        if (txtFileValue !== editedTxtFileValue) {
            const file = new File([editedTxtFileValue], filteredResource.name);
            handleSetFileUploadState({ files: [file], versionGroup: filteredResource.version_group, versionByEdit: true });
        }
    };

    return (
        <Transition appear show={previewModalStatus} as={Fragment}>
            <Dialog
                as="div"
                className={`relative`}
                style={{ zIndex: zIndexValue }}
                onClose={() => handleClearAllSelectedResources()}
                onClick={handleClick}
            >
                <div
                    className={`fixed flex flex-col justify-between w-[100vw] inset-0 bg-black/60 text-white`}
                    aria-hidden="true"
                    onContextMenu={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                    }}
                >
                    <header className={`previewModal-header w-full flex p-5 items-center z-30 h-[70px]`} id="click-close-modal">
                        <div
                            className={`${authStatus === "INVALID" && "hidden"} transition-all rounded-[10px] py-2 flex items-center gap-2 cursor-pointer w-[25px] h-[25px] md:w-[32px] md:h-[32px]`}
                        >
                            <More onClick={handleClickMore} />
                        </div>
                        <img
                            src={download}
                            alt="download"
                            className={`w-[25px] h-[25px] md:w-[32px] md:h-[32px] mx-4 cursor-pointer ${getFileExtension(selectedResources[0].name) === "pdf" && "opacity-0"}`}
                            onClick={handleDownload}
                        />
                        {resourceReader !== "txt" &&
                            formatsHasPrint.includes(separateNameAndFormat(filteredResource.name).format?.toLocaleLowerCase()) && (
                                <img
                                    src={printer}
                                    alt="printer"
                                    className={`cursor-pointer ml-2 w-[25px] h-[25px] md:w-[32px] md:h-[32px] ${getFileExtension(selectedResources[0].name) === "pdf" && "opacity-0"}`}
                                    onClick={handlePrint}
                                    id="preview-print-button"
                                />
                            )}

                        {resourceReader === "txt" && (
                            <ReactToPrint
                                trigger={() =>
                                    formatsHasPrint.includes(separateNameAndFormat(filteredResource.name).format?.toLocaleLowerCase()) && (
                                        <img src={printer} alt="printer" className="cursor-pointer ml-2 w-[25px] h-[25px] md:w-[32px] md:h-[32px]" />
                                    )
                                }
                                content={() => componentRef.current}
                                pageStyle="bg-none text-14 max-w-full"
                                bodyClass="max-w-full"
                            />
                        )}

                        {resourceReader === "txt" && (
                            <img
                                src={save}
                                alt="save"
                                className="cursor-pointer mr-3 w-[25px] h-[25px] md:w-[32px] md:h-[32px]"
                                onClick={() => exportNewTextFile()}
                            />
                        )}
                        <div className="flex mr-auto grow w-[50px] sm:w-auto" style={{ direction: "ltr" }}>
                            <p className="whitespace-nowrap overflow-hidden text-ellipsis text-left md:w-auto">
                                {separateNameAndFormat(filteredResource.name).name}
                            </p>
                            <p className="ml-[1px]">.{separateNameAndFormat(filteredResource.name).format}</p>
                        </div>
                        <img className="w-[20px] h-[20px] mx-3" src={setIconFile(filteredResource.name)} alt="icon" />
                        <img
                            src={arrowLeft}
                            alt="arrow-left"
                            onClick={() => {
                                if (informationModalStatus) {
                                    handleSetPreviewModalStatus(false, true);
                                    const qs = new URLSearchParams(location.search);
                                    qs.delete("preview");
                                    setSearchParams(qs);
                                } else {
                                    handleSetPreviewModalStatus(false);
                                    const qs = new URLSearchParams(location.search);
                                    qs.delete("preview");
                                    setSearchParams(qs);
                                }
                            }}
                            className="cursor-pointer w-[25px] h-[25px] md:w-[32px] md:h-[32px]"
                        />
                    </header>
                    <>
                        {filteredResources.length > 1 && (
                            <div
                                className={`z-50 fixed top-[48vh] right-6 inline-block p-3 cursor-pointer tooltip-parent ${getFileExtension(resource.name) === "pdf" && "opacity-0 pointer-events-none lg:pointer-events-auto lg:opacity-100"}`}
                            >
                                <span className="tooltip-child">قبلی</span>
                                <img
                                    src={backward}
                                    alt="backward"
                                    id="preview-previous-btn"
                                    className={`z-10 ${resourceIndex === 0 && "opacity-50 cursor-not-allowed"}`}
                                    onClick={() => handleChangeResource("previous")}
                                />
                            </div>
                        )}
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <div className="h-[100vh] flex flex-col items-center justify-center pb-[40px]" id="click-close-modal">
                                {resourceReader === "image" && (
                                    <ImageReader resource={filteredResource} scale={scale} setScale={setScale} ref={componentRef} />
                                )}

                                {imageFormatList.includes(getFileExtension(filteredResource.name)!) &&
                                    getFileExtension(filteredResource.name) === "svg" && (
                                        <SVGReader src={filteredResource.download_url} name={filteredResource.name} ref={componentRef} />
                                    )}

                                {resourceReader === "audio" && (
                                    <div style={{ direction: "ltr" }}>
                                        <AudioPlayer
                                            src={filteredResource.download_url}
                                            title={filteredResource.name}
                                            artistImage={filteredResource.has_cover ? `${filteredResource.download_url}?thumb=true` : undefined}
                                        />
                                    </div>
                                )}

                                {resourceReader === "video" && (
                                    <iframe
                                        title="video-player"
                                        className="mx-auto w-[90vw] !h-[84vh]"
                                        allowFullScreen
                                        src={`https://abrehamrahi.ir/ngp?type=${previewVideoFormats(getFileExtension(filteredResource.name)!)}&e-url=${encode(filteredResource.download_url)}${filteredResource.has_cover ? `&e-cover=${encode(`${filteredResource.download_url}?thumb=true`)}` : ""}`}
                                    />
                                )}

                                {resourceReader === "pdf" && (
                                    <Suspense fallback={<div>loading</div>}>
                                        <PdfReader pdfSource={filteredResource.download_url} ref={componentRef} />
                                    </Suspense>
                                )}

                                {resourceReader === "txt" && (
                                    <TxtReader
                                        textOnChange={(value: string) => setEditedTxtFileValue(value)}
                                        txtFileValue={txtFileValue}
                                        isLoading={isLoading}
                                    />
                                )}
                                {resourceReader === "txt" && (
                                    <div className="hidden">
                                        <pre
                                            dir="auto"
                                            ref={componentRef}
                                            style={{ fontFamily: "IranYekanFa" }}
                                            className="text-14 whitespace-pre-line max-w-full"
                                        >
                                            {txtFileValue}
                                        </pre>
                                    </div>
                                )}
                            </div>
                        </Transition.Child>

                        {filteredResources.length > 1 && (
                            <div
                                className={`fixed top-[48vh] left-6 inline-block p-3 cursor-pointer tooltip-parent ${getFileExtension(resource.name) === "pdf" && "opacity-0 pointer-events-none lg:pointer-events-auto lg:opacity-100"}`}
                            >
                                <span className="tooltip-child"> بعدی</span>
                                <img
                                    src={forward}
                                    alt="forward"
                                    id="preview-next-btn"
                                    className={`${resourceIndex === filteredResources.length - 1 && "opacity-50 cursor-not-allowed"}`}
                                    onClick={() => handleChangeResource("next")}
                                />
                            </div>
                        )}
                    </>
                </div>
                <DownloadAlertModal />
            </Dialog>
        </Transition>
    );
};

export default PreviewModal;
